import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import StatusIndicatorBar from '../../components/ui/StatusIndicatorBar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

import ProductCard from './components/ProductCard';
import ProductEditorModal from '../ecommerce-hub/components/ProductEditorModal';
import TabNavigation from './components/TabNavigation';
import AIFloatingButton from '../../components/AI/AIFloatingButton';


import { productService } from '../../services/productService';
import { triggerAutoReload } from '../../utils/autoReload';

const ProductCatalogManager = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [activeTab, setActiveTab] = useState('products');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalProducts, setTotalProducts] = useState(0);

  // Check for tab parameter on mount
  useEffect(() => {
    const tabParam = searchParams?.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * itemsPerPage;
      
      // Load products based on active tab
      let allProducts = [];
      if (activeTab === 'kits') {
        allProducts = await productService?.getByType('kit');
      } else if (activeTab === 'grouped') {
        // Grouped products are stored as 'product' type with metadata.groupType
        const products = await productService?.getByType('product');
        allProducts = products?.filter(p => p?.metadata?.groupType) || [];
      } else {
        // Regular products (exclude grouped ones)
        const products = await productService?.getByType('product');
        allProducts = products?.filter(p => !p?.metadata?.groupType) || [];
      }
      
      const result = {
        products: allProducts?.slice(offset, offset + itemsPerPage),
        total: allProducts?.length
      };
      
      console.log('Products loaded from Supabase:', result?.products?.length);
      console.log('Total products:', result?.total);
     console.log(result) 
      
      setProducts(result?.products);
      setFilteredProducts(result?.products);
      setTotalProducts(result?.total);
      setError('');
    } catch (err) {
      console.error('Error loading products:', err);
      
      let errorMessage = err?.message || 'Failed to load products';
      
      if (errorMessage?.includes('schema cache') || errorMessage?.includes('PGRST002') || errorMessage?.includes('paused')) {
        errorMessage = '⚠️ Database Connection Issue: Your Supabase project appears to be paused or unreachable. Please visit your Supabase dashboard to check the project status and resume it if needed.';
      }
      
      setError(errorMessage);
      setProducts([]);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let unsubscribe = null;

    if (isMounted) {
      loadProducts();
    }

    // Subscribe to real-time changes
    unsubscribe = productService?.subscribeToChanges(({ eventType, product }) => {
      if (!isMounted) return;

      if (eventType === 'INSERT') {
        setProducts(prev => [product, ...prev]);
        setTotalProducts(prev => prev + 1);
      } else if (eventType === 'UPDATE') {
        setProducts(prev => prev?.map(p => p?.id === product?.id ? product : p));
      } else if (eventType === 'DELETE') {
        setProducts(prev => prev?.filter(p => p?.id !== product?.id));
        setTotalProducts(prev => prev - 1);
      }
    });

    // Listen for auto-reload events
    const handleAutoReload = () => {
      if (isMounted) {
        loadProducts();
      }
    };
    window.addEventListener('auto-reload', handleAutoReload);

    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
      window.removeEventListener('auto-reload', handleAutoReload);
    };
  }, [currentPage, itemsPerPage, activeTab]);

  const applyFilters = (productList) => {
    let filtered = productList;

    // Apply search
    if (searchQuery) {
      filtered = filtered?.filter(p => 
        p?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        p?.sku?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Apply other filters
    if (filters?.category) {
      filtered = filtered?.filter(p => p?.category === filters?.category);
    }
    if (filters?.status) {
      filtered = filtered?.filter(p => p?.status === filters?.status);
    }

    setFilteredProducts(filtered);
  };

  useEffect(() => {
    applyFilters(products);
  }, [searchQuery, filters, products]);

  const displayedProducts = filteredProducts;

  const totalPages = Math.ceil(totalProducts / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalProducts);

  const handleDeleteProduct = async (product) => {
    try {
      await productService?.delete(product?.id);
      
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'success',
          message: `Produto "${product?.name}" excluído com sucesso`,
          progress: 100
        }
      }));
      
      // Trigger auto-reload
      triggerAutoReload('product-deleted');
    } catch (err) {
      console.error('Error deleting product:', err);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: 'Erro ao excluir produto',
          progress: 0
        }
      }));
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => {
      if (prev?.includes(productId)) {
        return prev?.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedProducts?.length === displayedProducts?.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(displayedProducts?.map(p => p?.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProducts?.length === 0) return;

    const confirmMessage = `Tem certeza que deseja excluir ${selectedProducts?.length} ${activeTab === 'products' ? 'produtos' : activeTab === 'kits' ? 'kits' : 'produtos agrupados'}?`;
    
    if (!window.confirm(confirmMessage)) return;

    setIsDeleting(true);

    try {
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'info',
          message: `Excluindo ${selectedProducts?.length} itens...`,
          progress: 50
        }
      }));

      // Delete all selected products
      await Promise.all(
        selectedProducts?.map(id => productService?.delete(id))
      );

      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'success',
          message: `${selectedProducts?.length} itens excluídos com sucesso`,
          progress: 100
        }
      }));

      setSelectedProducts([]);
      
      // Trigger auto-reload
      triggerAutoReload('bulk-delete');
    } catch (err) {
      console.error('Error bulk deleting:', err);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: 'Erro ao excluir itens selecionados',
          progress: 0
        }
      }));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteAll = async () => {
    const tabName = activeTab === 'products' ? 'produtos' : activeTab === 'kits' ? 'kits' : 'produtos agrupados';
    const confirmMessage = `⚠️ ATENÇÃO: Esta ação irá excluir TODOS os ${totalProducts} ${tabName} desta aba SEM EXCEÇÃO.\n\nEsta ação NÃO pode ser desfeita!\n\nDigite "EXCLUIR TUDO" para confirmar:`;
    
    const userInput = window.prompt(confirmMessage);
    
    if (userInput !== 'EXCLUIR TUDO') {
      if (userInput !== null) {
        window.dispatchEvent(new CustomEvent('statusUpdate', {
          detail: {
            type: 'error',
            message: 'Confirmação incorreta. Operação cancelada.',
            progress: 0
          }
        }));
      }
      return;
    }

    setIsDeleting(true);

    try {
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'info',
          message: `Excluindo todos os ${totalProducts} ${tabName}...`,
          progress: 30
        }
      }));

      // Load ALL products from current tab (not just paginated)
      let allProductsToDelete = [];
      if (activeTab === 'kits') {
        allProductsToDelete = await productService?.getByType('kit');
      } else if (activeTab === 'grouped') {
        const products = await productService?.getByType('product');
        allProductsToDelete = products?.filter(p => p?.metadata?.groupType) || [];
      } else {
        const products = await productService?.getByType('product');
        allProductsToDelete = products?.filter(p => !p?.metadata?.groupType) || [];
      }

      // Delete all products in batches
      const batchSize = 10;
      for (let i = 0; i < allProductsToDelete?.length; i += batchSize) {
        const batch = allProductsToDelete?.slice(i, i + batchSize);
        await Promise.all(batch?.map(p => productService?.delete(p?.id)));
        
        const progress = Math.min(30 + ((i + batchSize) / allProductsToDelete?.length) * 60, 90);
        window.dispatchEvent(new CustomEvent('statusUpdate', {
          detail: {
            type: 'info',
            message: `Excluindo ${tabName}... ${Math.min(i + batchSize, allProductsToDelete?.length)}/${allProductsToDelete?.length}`,
            progress
          }
        }));
      }

      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'success',
          message: `Todos os ${allProductsToDelete?.length} ${tabName} foram excluídos com sucesso`,
          progress: 100
        }
      }));

      setSelectedProducts([]);
      
      // Trigger auto-reload
      triggerAutoReload('delete-all');
    } catch (err) {
      console.error('Error deleting all:', err);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: 'Erro ao excluir todos os itens',
          progress: 0
        }
      }));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveProduct = async (updatedProduct) => {
    try {
      await productService?.update(updatedProduct?.id, updatedProduct);
      
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'success',
          message: 'Produto atualizado com sucesso',
          progress: 100
        }
      }));
      
      setEditingProduct(null);
      
      // Trigger auto-reload
      triggerAutoReload('product-updated');
    } catch (err) {
      console.error('Error updating product:', err);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: 'Erro ao atualizar produto',
          details: err?.message
        }
      }));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedProducts?.length === 0) return;

    const confirmMessage = `Tem certeza que deseja excluir ${selectedProducts?.length} ${activeTab === 'products' ? 'produtos' : activeTab === 'kits' ? 'kits' : 'produtos agrupados'}?`;
    
    if (!window.confirm(confirmMessage)) return;

    setIsDeleting(true);

    try {
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'info',
          message: `Excluindo ${selectedProducts?.length} itens...`,
          progress: 50
        }
      }));

      // Delete all selected products
      for (const productId of selectedProducts) {
        await productService?.delete(productId);
      }

      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'success',
          message: `${selectedProducts?.length} produto(s) excluído(s) com sucesso`,
          progress: 100
        }
      }));

      setSelectedProducts([]);
      
      // Trigger auto-reload
      triggerAutoReload('selected-deleted');
    } catch (err) {
      console.error('Error bulk deleting:', err);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: 'Erro ao excluir itens selecionados',
          progress: 0
        }
      }));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <StatusIndicatorBar />
      <main className="pt-16">
        <div className="px-4 lg:px-6 py-6 lg:py-8">
          <BreadcrumbNavigation />

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                Catálogo de Produtos
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Visualize seus produtos importados
              </p>
            </div>

            <div className="flex items-center gap-2">
              {selectedProducts?.length > 0 && (
                <Button
                  variant="destructive"
                  size="default"
                  iconName="Trash2"
                  iconPosition="left"
                  onClick={handleBulkDelete}
                  disabled={isDeleting}
                >
                  Excluir {selectedProducts?.length} {selectedProducts?.length === 1 ? 'item' : 'itens'}
                </Button>
              )}
              {totalProducts > 0 && (
                <Button
                  variant="destructive"
                  size="default"
                  iconName="Trash"
                  iconPosition="left"
                  onClick={handleDeleteAll}
                  disabled={isDeleting}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Excluir Todos ({totalProducts})
                </Button>
              )}
              <Button
                variant="default"
                size="default"
                iconName="Upload"
                iconPosition="left"
                onClick={() => navigate('/csv-import-dashboard')}>
                Importar CSV
              </Button>
            </div>
          </div>

          {/* Tab Navigation */}
          <TabNavigation
            activeTab={activeTab}
            onTabChange={setActiveTab}
            counts={{
              products: activeTab === 'products' ? totalProducts : 0,
              kits: activeTab === 'kits' ? totalProducts : 0,
              grouped: activeTab === 'grouped' ? totalProducts : 0
            }}
          />

          <div className="mt-6 space-y-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <Input
                  type="search"
                  placeholder="Buscar por nome ou SKU..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)} />
              </div>
              {displayedProducts?.length > 0 && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedProducts?.length === displayedProducts?.length && displayedProducts?.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-muted-foreground">
                    Selecionar todos
                  </span>
                </div>
              )}
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 lg:py-24">
                <div className="flex items-center justify-center w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-muted mb-4">
                  <Icon name="Loader" size={32} className="text-muted-foreground animate-spin" />
                </div>
                <h3 className="text-lg lg:text-xl font-semibold text-foreground mb-2">
                  Carregando produtos...
                </h3>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-16 lg:py-24">
                <div className="flex items-center justify-center w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-destructive/10 mb-4">
                  <Icon name="AlertCircle" size={32} className="text-destructive" />
                </div>
                <h3 className="text-lg lg:text-xl font-semibold text-foreground mb-2">
                  Erro ao carregar produtos
                </h3>
                <p className="text-sm lg:text-base text-muted-foreground text-center max-w-md mb-6">
                  {error}
                </p>
              </div>
            ) : filteredProducts?.length === 0 ?
            <div className="flex flex-col items-center justify-center py-16 lg:py-24">
                <div className="flex items-center justify-center w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-muted mb-4">
                  <Icon name="Package" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg lg:text-xl font-semibold text-foreground mb-2">
                  Nenhum produto encontrado
                </h3>
                <p className="text-sm lg:text-base text-muted-foreground text-center max-w-md mb-6">
                  Ajuste os filtros ou importe novos produtos para começar
                </p>
                <Button
                variant="default"
                iconName="Upload"
                iconPosition="left"
                onClick={() => navigate('/csv-import-dashboard')}>
                  Importar Produtos
                </Button>
              </div> :

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {displayedProducts?.map((product) => (
                <ProductCard
                  key={product?.id}
                  product={product}
                  isSelected={selectedProducts?.includes(product?.id)}
                  onSelect={() => handleSelectProduct(product?.id)}
                  onEdit={(product) => setEditingProduct(product)}
                  onDelete={handleDeleteProduct}
                  onPreview={() => {}}
                  onQuickEdit={() => {}} />
              ))}
            </div>
            }
            
            {/* Pagination Controls */}
            {!loading && !error && filteredProducts?.length > 0 && (
              <div className="flex flex-col lg:flex-row items-center justify-between gap-4 pt-6 border-t border-border">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Itens por página:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e?.target?.value));
                      setCurrentPage(1);
                    }}
                    className="px-3 py-1.5 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                
                <div className="text-sm text-muted-foreground">
                  Mostrando {startItem}–{endItem} de {totalProducts} produtos
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ChevronLeft"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  />
                  
                  <span className="text-sm text-foreground px-3">
                    Página {currentPage} de {totalPages}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ChevronRight"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Product Editor Modal */}
      {editingProduct && (
        <ProductEditorModal
          isOpen={true}
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          onSave={handleSaveProduct}
        />
      )}

      {/* AI Floating Button */}
      <AIFloatingButton />
    </div>
  );
};

export default ProductCatalogManager;